import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useTechnology } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { TechnologyForm } from '../../components/technologies';
import { updateTechnology, ApiError, type UpdateTechnologyInput } from '../../lib/apiClient';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import type { CreateTechnologyFormData } from '../../components/forms/validation';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './edit.css';

export const Route = createFileRoute('/technologies/$technologyId/edit')({
  component: EditTechnologyPage,
});

export { EditTechnologyPage };

function EditTechnologyPage(): JSX.Element {
  const { technologyId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, error: fetchError } = useTechnology(technologyId);
  const technology = data?.technology;

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const handleSubmit = async (data: CreateTechnologyFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const input: UpdateTechnologyInput = {
        name: data.name,
        description: data.description || undefined,
        productFamilyId: data.productFamilyId || undefined,
        version: data.version || undefined,
        lifecycleStage: data.lifecycleStage || undefined,
        supplierId: data.supplierId || undefined,
        licenseType: (data.licenseType as string | undefined) || undefined,
        supportEndDate: data.supportEndDate || undefined,
        securityRating: (data.securityRating as string | undefined) || undefined,
        notes: data.notes || undefined,
      };
      await updateTechnology(technologyId, input);
      await apolloClient.refetchQueries({
        include: ['GetTechnology', 'ListTechnologies'],
      });
      void navigate({
        to: '/technologies/$technologyId',
        params: { technologyId },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({ to: '/technologies/$technologyId', params: { technologyId } });
  };

  if (loading) {
    return (
      <div className="edit-technology-page">
        <LoadingSpinner centered message="Loading technology..." />
      </div>
    );
  }

  if (fetchError || !technology) {
    return (
      <div className="edit-technology-page">
        <EmptyState
          icon="error"
          title="Failed to load technology"
          description={fetchError?.message || 'Technology not found'}
          actionLabel="Back to Technologies"
          onAction={() =>
            void navigate({
              to: '/technologies',
              search: { page: 1, pageSize: 25, supplierId: '', productFamilyId: '' },
            })
          }
        />
      </div>
    );
  }

  const defaultValues: Partial<CreateTechnologyFormData> = {
    name: technology.name,
    description: technology.description || undefined,
    productFamilyId: technology.productFamilyId || undefined,
    version: technology.version || undefined,
    lifecycleStage: technology.lifecycleStage,
    supplierId: technology.supplierId || undefined,
    licenseType: (technology.licenseType as CreateTechnologyFormData['licenseType']) || undefined,
    supportEndDate: technology.supportEndDate ? technology.supportEndDate.split('T')[0] : undefined,
    securityRating:
      (technology.securityRating as CreateTechnologyFormData['securityRating']) || undefined,
    notes: technology.notes || undefined,
  };

  return (
    <div className="edit-technology-page">
      <div className="breadcrumb">
        <Link
          to="/technologies/$technologyId"
          params={{ technologyId }}
          className="breadcrumb-link"
        >
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to {technology.name}</span>
        </Link>
      </div>

      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Technology</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update the details for {technology.name}
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TechnologyForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
